import { useAuth0 } from '@auth0/auth0-react';
import { enqueueSnackbar } from 'notistack';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { SplashScreen } from 'src/components/loading-screen';
import { CurrentUserResponse, useCurrentUserQuery, useSignoutMutation } from 'src/context/api/auth';
import Auth from 'src/pages/auth';
import { useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';

const authPaths = ['/auth/signin', '/auth/password', '/auth/forgot', '/auth/reset_password', '/auth/eidp', '/onboard/success']

interface UserContextType {
  user: CurrentUserResponse | null
  reload: () => void
}

export const userCtx = createContext<UserContextType>({ user: null, reload: () => { } })

export const useAuth = () => {
  const { user, reload } = useContext(userCtx)

  return {
    user: user,
    isAuthenticated: !!user,
    reload: reload
  }
}


const useSession = () => {
  const router = useRouter()
  const [sessionActive, setSessionActive] = useState(false);
  const { data: user } = useCurrentUserQuery()
  const [code, setCode]: any = useState(null)

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "sessionExpired") {
        // Rediriger proprement vers la page de connexion
        setSessionActive(false);
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);


  useEffect(() => {
    if (authPaths.includes(window.location.pathname)) {
      return; // Ne pas vérifier la session si on est sur la page de connexion
    } else {

      let intervalRef: any;

      const checkSession = async () => {
        try {
          const response = await fetch(`${process.env.CROSS_API_URL}` + "/session-status", { credentials: "include" });

          if (response.status === 401) {
            setSessionActive(false);
            setCode(response.status)
            clearInterval(intervalRef)

            //  Stocker l'info dans localStorage pour alerter les autres onglets
            localStorage.setItem("sessionExpired", Date.now().toString());

          } else {
            setSessionActive(true);
          }
        } catch (error) {
          setSessionActive(false);
        }
      };

      intervalRef = setInterval(checkSession, 180000); //Vérifie toutes les  1200000 ms (20min) 
      return () => clearInterval(intervalRef);
    }
  }, [user, sessionActive, window.location.pathname]);

  return { 'sessionActive': sessionActive, code: code };
};


export default function AuthGuard(props: Props) {
  const router = useRouter()
  const [auth, setAuth] = useState(false);
  const { isAuthenticated, user: auth0User, isLoading: isLoadingAuth0 } = useAuth0();
  const { data: user = null, isLoading, isFetching, isError, error, refetch: reload } = useCurrentUserQuery();

  const sessionActive = useSession();


  useEffect(() => {
    if ((!sessionActive.sessionActive)) {
      if (authPaths.includes(window.location.pathname)) {
        return
      } else {
        if (!sessionActive.sessionActive && sessionActive?.code === 401) {
          enqueueSnackbar("Session expired, please sign in again!", { variant: "warning" });
          window.location.href = '/auth/signin'
        }
      }
    }
  }, [sessionActive, window.location.pathname])

  const checkAuth = useCallback(() => {
    if (user) {
      setAuth(true)

      if (authPaths.includes(window.location.pathname)) {
        if (user?.workspaces?.length && user?.workspaces?.length > 1 || user.role === "admin") {
          router.push("/")
        } else if (user?.workspaces?.length === 1) {
          router.push(paths.dash.searchPerformanceOverview + '?workspace=' + user.workspaces[0].id)
        } else {
          router.push(paths.dash.searchPerformanceOverview)
        }
      }

    } else {
      setAuth(false)
    }

  }, [user, error, auth0User, isAuthenticated])

  useEffect(() => {
    checkAuth()
  }, [user, error]);

  return (
    <userCtx.Provider value={{ user: user, reload }}>
      {isLoading || isFetching ?
        <SplashScreen />
        :
          (!auth && !isLoading && !isFetching && !user) ?
            <Auth />
            :
            props.children
      }
    </userCtx.Provider>
  )
}

interface Props {
  children: React.ReactNode;
}
